import React, { useState, useEffect } from 'react';
import { SafeAreaView, View, Text, StyleSheet, TouchableOpacity, Dimensions, ScrollView, RefreshControl, StatusBar, Alert, Modal, FlatList, ActivityIndicator, TextInput } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

const { width } = Dimensions.get('window');
const PRIMARY = '#0e4e96'; const SECONDARY = '#1e7be2';
const API_BASE_URL = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const api = axios.create({ baseURL: API_BASE_URL, timeout: 15000, headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-API-Key': API_KEY } });
api.interceptors.request.use(async c => { try { const t = await AsyncStorage.getItem('auth_token')||await AsyncStorage.getItem('token'); if(t)c.headers.Authorization=`Bearer ${t}`; }catch{} return c; });
const EP = { companyProfile:(id)=>`/api/company-profile/${id}`, deals:'/api/data/crm_deals', contacts:'/api/data/crm_contacts', stages:'/api/data/crm_stages', leadScores:'/api/data/lead_scores' };
const getBusinessId=async()=>{try{return await AsyncStorage.getItem('company_id')||await AsyncStorage.getItem('company_id')||'0';}catch{return '0';}};
const safeData=r=>(r?.data?.success&&Array.isArray(r.data.data))?r.data.data:[];

export default function LeadScoringScreen({ navigation, route }) {
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('leads');
  const [showScoringModal, setShowScoringModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [scoredLeads, setScoredLeads] = useState([]);
  const [scoringCriteria] = useState([
    { id:'1', name:'Budget Authority', weight:25, desc:'Has budget approval power' },
    { id:'2', name:'Decision Maker', weight:20, desc:'Key decision maker in org' },
    { id:'3', name:'Need Identified', weight:20, desc:'Clear need for product/service' },
    { id:'4', name:'Timeline Urgency', weight:15, desc:'Active buying timeline' },
    { id:'5', name:'Company Size', weight:10, desc:'Company size matches ICP' },
    { id:'6', name:'Engagement Level', weight:10, desc:'Active engagement with content' },
  ]);
  const [scoreDistribution, setScoreDistribution] = useState({ hot:0, warm:0, cold:0, unscored:0 });

  useEffect(()=>{init();},[]);
  const init=async()=>{try{setIsLoading(true);const b=route?.params?.company_id||await getBusinessId();setCompanyId(b);await fetchAll(b);}catch(e){console.error(e);}finally{setIsLoading(false);}};
  const onRefresh=async()=>{setRefreshing(true);await init();setRefreshing(false);};

  const fetchAll=async(bizId)=>{
    const cid=parseInt(bizId)||0;const p={company_id:cid};
    const [profileRes,dealsRes,contactsRes,scoresRes]=await Promise.allSettled([
      api.get(EP.companyProfile(cid)),api.get(EP.deals,{params:p}),api.get(EP.contacts,{params:p}),api.get(EP.leadScores,{params:p}),
    ]);
    const profile=profileRes.status==='fulfilled'?(profileRes.value?.data?.success?(Array.isArray(profileRes.value.data.data)?profileRes.value.data.data[0]:profileRes.value.data.data):null):null;
    if(profile){setCompanyProfile(profile);await AsyncStorage.setItem('company_profile',JSON.stringify(profile));}
    else{try{const c=await AsyncStorage.getItem('company_profile');if(c)setCompanyProfile(JSON.parse(c));}catch{}}

    const deals=safeData(dealsRes.status==='fulfilled'?dealsRes.value:null);
    const contacts=safeData(contactsRes.status==='fulfilled'?contactsRes.value:null);
    const scores=safeData(scoresRes.status==='fulfilled'?scoresRes.value:null);

    // Build scored leads from deals + contacts
    const scored=deals.map(d=>{
      const contact=contacts.find(c=>c.email===d.contact_email);
      const existingScore=contact?.score||0;
      // Auto-score based on available data
      let score=existingScore;
      if(score===0){
        score=0;
        if(parseFloat(d.value||0)>20000)score+=25; else if(parseFloat(d.value||0)>5000)score+=15; else score+=5;
        if(d.probability>50)score+=20; else if(d.probability>20)score+=10;
        if(d.stage&&!['lead','new'].includes(d.stage))score+=15;
        if(d.contact_email)score+=10;
        if(d.contact_phone)score+=5;
        if(d.notes)score+=5;
        score=Math.min(score,100);
      }
      return {
        id:String(d.id), name:d.contact_name||'Unknown', email:d.contact_email||'',
        phone:d.contact_phone||'', company:d.company||'', stage:d.stage||'lead',
        value:parseFloat(d.value||0), probability:d.probability||0, score,
        category:score>=70?'hot':score>=40?'warm':'cold',
        criteria:{budget:parseFloat(d.value||0)>10000, decisionMaker:d.probability>50,
          needIdentified:d.stage!=='lead', timeline:d.probability>30,
          companySize:true, engagement:!!d.notes},
        source:d.source||'direct', createdAt:d.created_at,
      };
    }).sort((a,b)=>b.score-a.score);

    setScoredLeads(scored);
    setScoreDistribution({
      hot:scored.filter(l=>l.category==='hot').length,
      warm:scored.filter(l=>l.category==='warm').length,
      cold:scored.filter(l=>l.category==='cold').length,
      unscored:0,
    });
  };

  const formatCurrency=v=>new Intl.NumberFormat('en-GB',{style:'currency',currency:companyProfile?.currency||'GBP',minimumFractionDigits:0,maximumFractionDigits:0}).format(v||0);
  const getScoreColor=s=>s>=70?'#10B981':s>=40?'#F59E0B':'#EF4444';
  const getCategoryIcon=c=>({hot:'fire',warm:'white-balance-sunny',cold:'snowflake'}[c]||'circle');

  const renderHeader=()=>(
    <LinearGradient colors={['#7C3AED','#8B5CF6']} style={ss.header}>
      <View style={ss.headerRow}>
        <TouchableOpacity onPress={()=>navigation?.goBack()} style={ss.headerBtn}><MaterialCommunityIcons name="arrow-left" size={24} color="#FFF"/></TouchableOpacity>
        <View style={ss.headerCenter}>
          <Text style={ss.headerTitle}>{companyProfile?.name||'Lead Scoring'}</Text>
          <Text style={ss.headerSub}>{scoredLeads.length} scored leads</Text>
        </View>
        <TouchableOpacity style={ss.headerBtn} onPress={()=>Alert.alert('Info','Scores auto-calculated from deal value, stage, probability & engagement')}><MaterialCommunityIcons name="information" size={24} color="#FFF"/></TouchableOpacity>
      </View>
    </LinearGradient>
  );

  const renderDistribution=()=>(
    <View style={ss.distRow}>
      {[{l:'Hot (70+)',v:scoreDistribution.hot,c:'#EF4444',i:'fire'},{l:'Warm (40-69)',v:scoreDistribution.warm,c:'#F59E0B',i:'white-balance-sunny'},{l:'Cold (<40)',v:scoreDistribution.cold,c:'#3B82F6',i:'snowflake'}].map((d,i)=>(
        <View key={i} style={ss.distCard}>
          <MaterialCommunityIcons name={d.i} size={24} color={d.c}/>
          <Text style={[ss.distValue,{color:d.c}]}>{d.v}</Text>
          <Text style={ss.distLabel}>{d.l}</Text>
        </View>
      ))}
    </View>
  );

  const renderTabs=()=>(
    <View style={ss.tabsRow}><ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{paddingHorizontal:12}}>
      {[{k:'leads',t:'Scored Leads',i:'format-list-numbered'},{k:'criteria',t:'Scoring Rules',i:'tune'},{k:'analytics',t:'Analytics',i:'chart-bar'}].map(tab=>(
        <TouchableOpacity key={tab.k} style={[ss.tab,activeTab===tab.k&&ss.activeTab]} onPress={()=>setActiveTab(tab.k)}>
          <MaterialCommunityIcons name={tab.i} size={18} color={activeTab===tab.k?'#7C3AED':'#6B7280'}/>
          <Text style={[ss.tabText,activeTab===tab.k&&{color:'#7C3AED',fontWeight:'600'}]}>{tab.t}</Text>
        </TouchableOpacity>
      ))}
    </ScrollView></View>
  );

  const renderScoredLeads=()=>scoredLeads.length===0?(
    <View style={ss.empty}><MaterialCommunityIcons name="format-list-numbered" size={48} color="#D1D5DB"/><Text style={ss.emptyText}>No leads to score</Text></View>
  ):(
    <View style={{padding:16}}>{scoredLeads.map((l,i)=>(
      <TouchableOpacity key={i} style={ss.leadCard} onPress={()=>{setSelectedLead(l);setShowScoringModal(true);}}>
        <View style={{flexDirection:'row',alignItems:'center'}}>
          <View style={[ss.scoreCircle,{borderColor:getScoreColor(l.score)}]}>
            <Text style={[ss.scoreNum,{color:getScoreColor(l.score)}]}>{l.score}</Text>
          </View>
          <View style={{flex:1,marginLeft:12}}>
            <Text style={ss.leadName}>{l.name}</Text>
            <Text style={ss.leadMeta}>{l.company} · {l.stage}</Text>
          </View>
          <View style={{alignItems:'flex-end'}}>
            <MaterialCommunityIcons name={getCategoryIcon(l.category)} size={20} color={getScoreColor(l.score)}/>
            {l.value>0&&<Text style={ss.leadValue}>{formatCurrency(l.value)}</Text>}
          </View>
        </View>
        <View style={ss.criteriaRow}>
          {Object.entries(l.criteria).map(([k,v],ci)=>(
            <View key={ci} style={[ss.critDot,{backgroundColor:v?'#10B981':'#E5E7EB'}]}>
              <MaterialCommunityIcons name={v?'check':'close'} size={10} color={v?'#FFF':'#9CA3AF'}/>
            </View>
          ))}
        </View>
      </TouchableOpacity>
    ))}</View>
  );

  const renderCriteria=()=>(
    <View style={ss.section}>
      <Text style={ss.sectionTitle}>Scoring Criteria</Text>
      <Text style={ss.sectionSub}>Total weight: {scoringCriteria.reduce((s2,c)=>s2+c.weight,0)}%</Text>
      {scoringCriteria.map((c,i)=>(
        <View key={i} style={ss.criteriaCard}>
          <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center'}}>
            <Text style={ss.criteriaName}>{c.name}</Text>
            <Text style={ss.criteriaWeight}>{c.weight}%</Text>
          </View>
          <Text style={ss.criteriaDesc}>{c.desc}</Text>
          <View style={ss.weightBar}><View style={[ss.weightFill,{width:`${c.weight}%`}]}/></View>
        </View>
      ))}
    </View>
  );

  const renderAnalytics=()=>{
    const avgScore=scoredLeads.length>0?scoredLeads.reduce((s2,l)=>s2+l.score,0)/scoredLeads.length:0;
    const hotValue=scoredLeads.filter(l=>l.category==='hot').reduce((s2,l)=>s2+l.value,0);
    const warmValue=scoredLeads.filter(l=>l.category==='warm').reduce((s2,l)=>s2+l.value,0);
    return (
      <View style={ss.section}>
        <Text style={ss.sectionTitle}>Score Analytics</Text>
        <View style={ss.analyticsGrid}>
          {[{l:'Avg Score',v:avgScore.toFixed(0),c:'#7C3AED'},{l:'Hot Pipeline',v:formatCurrency(hotValue),c:'#EF4444'},{l:'Warm Pipeline',v:formatCurrency(warmValue),c:'#F59E0B'},{l:'Total Leads',v:String(scoredLeads.length),c:'#3B82F6'}].map((m,i)=>(
            <View key={i} style={ss.analyticsCard}><Text style={[ss.analyticsValue,{color:m.c}]}>{m.v}</Text><Text style={ss.analyticsLabel}>{m.l}</Text></View>
          ))}
        </View>
      </View>
    );
  };

  const renderModal=()=>(
    <Modal visible={showScoringModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={()=>setShowScoringModal(false)}>
      <View style={ss.modalContainer}>
        <View style={ss.modalHeader}>
          <Text style={ss.modalTitle}>{selectedLead?.name||'Lead Score'}</Text>
          <TouchableOpacity style={ss.modalClose} onPress={()=>setShowScoringModal(false)}><MaterialCommunityIcons name="close" size={24} color="#6B7280"/></TouchableOpacity>
        </View>
        {selectedLead&&(
          <ScrollView style={{flex:1,padding:16}}>
            <View style={[ss.bigScore,{borderColor:getScoreColor(selectedLead.score)}]}>
              <Text style={[ss.bigScoreNum,{color:getScoreColor(selectedLead.score)}]}>{selectedLead.score}</Text>
              <Text style={ss.bigScoreLabel}>{String(selectedLead.category ?? '').toUpperCase()} LEAD</Text>
            </View>
            <View style={ss.modalSection}><Text style={ss.modalSectionTitle}>Contact</Text>
              <Text style={ss.modalText}>Company: {selectedLead.company}</Text>
              <Text style={ss.modalText}>Email: {selectedLead.email}</Text>
              <Text style={ss.modalText}>Value: {formatCurrency(selectedLead.value)}</Text>
              <Text style={ss.modalText}>Stage: {selectedLead.stage}</Text>
              <Text style={ss.modalText}>Probability: {selectedLead.probability}%</Text>
            </View>
            <View style={ss.modalSection}><Text style={ss.modalSectionTitle}>Scoring Breakdown</Text>
              {scoringCriteria.map((c,i)=>{
                const met=selectedLead.criteria[Object.keys(selectedLead.criteria)[i]];
                return <View key={i} style={ss.criteriaResult}><MaterialCommunityIcons name={met?'check-circle':'close-circle'} size={18} color={met?'#10B981':'#EF4444'}/><Text style={ss.criteriaResultText}>{c.name} ({c.weight}%)</Text></View>;
              })}
            </View>
            {companyProfile&&<View style={ss.modalSection}><Text style={ss.modalSectionTitle}>Company</Text><Text style={ss.modalText}>{companyProfile.name}{companyProfile.industry?` · ${companyProfile.industry}`:''}</Text></View>}
          </ScrollView>
        )}
      </View>
    </Modal>
  );

  const renderContent=()=>{
    const wrap=ch=><ScrollView style={{flex:1}} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#7C3AED']}/>} showsVerticalScrollIndicator={false}>{ch}<View style={{height:40}}/></ScrollView>;
    switch(activeTab){case 'leads':return wrap(renderScoredLeads());case 'criteria':return wrap(renderCriteria());case 'analytics':return wrap(renderAnalytics());default:return null;}
  };

  if(isLoading)return <SafeAreaView style={ss.loading}><StatusBar barStyle="dark-content"/><ActivityIndicator size="large" color="#7C3AED"/><Text style={ss.loadingText}>Scoring leads...</Text></SafeAreaView>;

  return (
    <SafeAreaView style={ss.container}><StatusBar barStyle="light-content"/>
      {renderHeader()}{renderDistribution()}{renderTabs()}{renderContent()}{renderModal()}
    </SafeAreaView>
  );
}

const ss=StyleSheet.create({
  container:{flex:1,backgroundColor:'#F3F4F6'},loading:{flex:1,justifyContent:'center',alignItems:'center',backgroundColor:'#F3F4F6'},loadingText:{fontSize:16,color:'#6B7280',marginTop:16},
  header:{paddingTop:8,paddingBottom:16,paddingHorizontal:16},headerRow:{flexDirection:'row',alignItems:'center'},headerBtn:{width:40,height:40,borderRadius:20,backgroundColor:'rgba(255,255,255,0.12)',justifyContent:'center',alignItems:'center'},headerCenter:{flex:1,marginLeft:12},headerTitle:{fontSize:20,fontWeight:'bold',color:'#FFF'},headerSub:{fontSize:13,color:'#DDD6FE',marginTop:2},
  distRow:{flexGrow:0,flexShrink:0,flexDirection:'row',backgroundColor:'#FFF',padding:12,borderBottomWidth:1,borderBottomColor:'#E5E7EB',justifyContent:'space-around'},distCard:{alignItems:'center'},distValue:{fontSize:20,fontWeight:'bold',marginTop:4},distLabel:{fontSize:11,color:'#6B7280',marginTop:2},
  tabsRow:{backgroundColor:'#FFF',borderBottomWidth:1,borderBottomColor:'#E5E7EB'},tab:{flexDirection:'row',alignItems:'center',paddingHorizontal:14,paddingVertical:10,marginRight:4,borderRadius:20},activeTab:{backgroundColor:'#F5F3FF'},tabText:{fontSize:13,color:'#6B7280',marginLeft:6,fontWeight:'500'},
  section:{backgroundColor:'#FFF',margin:16,padding:16,borderRadius:12,borderWidth:1,borderColor:'#E5E7EB'},sectionTitle:{fontSize:17,fontWeight:'bold',color:'#1F2937',marginBottom:12},sectionSub:{fontSize:12,color:'#6B7280',marginBottom:8},
  empty:{alignItems:'center',padding:32},emptyText:{fontSize:16,color:'#6B7280',marginTop:12},
  leadCard:{backgroundColor:'#FFF',marginBottom:8,padding:14,borderRadius:10,borderWidth:1,borderColor:'#E5E7EB'},leadName:{fontSize:15,fontWeight:'600',color:'#1F2937'},leadMeta:{fontSize:12,color:'#6B7280',marginTop:1},leadValue:{fontSize:13,fontWeight:'600',color:'#10B981',marginTop:2},
  scoreCircle:{width:48,height:48,borderRadius:24,borderWidth:3,justifyContent:'center',alignItems:'center'},scoreNum:{fontSize:18,fontWeight:'bold'},
  criteriaRow:{flexDirection:'row',marginTop:8,gap:4},critDot:{width:18,height:18,borderRadius:9,justifyContent:'center',alignItems:'center'},
  criteriaCard:{backgroundColor:'#F9FAFB',borderRadius:8,padding:12,marginBottom:8},criteriaName:{fontSize:15,fontWeight:'600',color:'#1F2937'},criteriaWeight:{fontSize:14,fontWeight:'700',color:'#7C3AED'},criteriaDesc:{fontSize:12,color:'#6B7280',marginTop:2},weightBar:{height:4,backgroundColor:'#E5E7EB',borderRadius:2,marginTop:6,overflow:'hidden'},weightFill:{height:'100%',backgroundColor:'#7C3AED',borderRadius:2},
  analyticsGrid:{flexDirection:'row',flexWrap:'wrap',justifyContent:'space-between'},analyticsCard:{width:(width-64)/2,backgroundColor:'#F9FAFB',borderRadius:10,padding:14,alignItems:'center',marginBottom:8},analyticsValue:{fontSize:18,fontWeight:'bold'},analyticsLabel:{fontSize:12,color:'#6B7280',marginTop:4},
  modalContainer:{flex:1,backgroundColor:'#FFF'},modalHeader:{flexDirection:'row',alignItems:'center',justifyContent:'space-between',padding:16,borderBottomWidth:1,borderBottomColor:'#E5E7EB'},modalTitle:{fontSize:18,fontWeight:'bold',color:'#1F2937',flex:1},modalClose:{width:40,height:40,borderRadius:20,backgroundColor:'#F3F4F6',justifyContent:'center',alignItems:'center'},
  bigScore:{width:100,height:100,borderRadius:50,borderWidth:4,justifyContent:'center',alignItems:'center',alignSelf:'center',marginBottom:20},bigScoreNum:{fontSize:36,fontWeight:'bold'},bigScoreLabel:{fontSize:10,fontWeight:'600',marginTop:-2},
  modalSection:{marginBottom:20},modalSectionTitle:{fontSize:16,fontWeight:'600',color:'#1F2937',marginBottom:8},modalText:{fontSize:14,color:'#6B7280',lineHeight:22},
  criteriaResult:{flexDirection:'row',alignItems:'center',marginBottom:6},criteriaResultText:{fontSize:14,color:'#1F2937',marginLeft:8},
});